import {
  useCreate,
  useDelete,
  useGetIdentity,
  useInvalidate,
  useList,
  useNavigation,
} from '@refinedev/core'
import { useForm, Controller } from 'react-hook-form'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Autocomplete,
  TextField,
  Chip,
  Link,
  Tooltip,
} from '@mui/material'
import { User } from '@/utils/auth/auth0'
import { UUID } from 'crypto'
import { useMemo, useState } from 'react'
import {
  AliasHint,
  formatGroupLocationsPreview,
  getAsciiName,
} from '../utils/textUtils'
import { RegionSet as RegionSetBase } from '../interfaces/regions'

interface Region {
  id: number
  name: string
  ascii_name?: string
  country?: string
  region?: string
  council?: string
  major?: string
  suburb?: string
  additional_names?: string[]
}

// isLocationSet is a local UI discriminant added when merging fetched
// region_sets into the combined RegionOption list -- the API itself never
// sends it (see RegionSetBase, the single source of truth for the wire shape).
type RegionSet = RegionSetBase & { isLocationSet: true }

type RegionOption = Region | RegionSet

const isRegionSet = (v: RegionOption): v is RegionSet => 'isLocationSet' in v

interface FunderRegionManagementProps {
  funderUuid: UUID
  regionData: any
  regionNamesData: any
  regionDataIsLoading: boolean
}

export default function FunderRegionManagement({
  funderUuid,
  regionData,
  regionNamesData,
  regionDataIsLoading,
}: FunderRegionManagementProps) {
  const { show } = useNavigation()
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm()
  const { mutate: createFunderRegion } = useCreate()
  const { mutate: deleteFunderRegion } = useDelete()

  const [saving, setSaving] = useState(false)

  const invalidate = useInvalidate()

  const { data: regionOptions } = useList({
    resource: 'regions',
    meta: {
      paginate: false,
    },
    sorters: [
      {
        field: 'id',
        order: 'asc',
      },
    ],
  })

  const { data: regionSetOptions } = useList({
    resource: 'region-sets',
  })

  const regionNameById = useMemo(
    () =>
      new Map(
        ((regionOptions?.data as Region[]) || []).map((r) => [r.id, r.name]),
      ),
    [regionOptions?.data],
  )

  const user = useGetIdentity().data as User

  function handleAddRegion(data: any) {
    const regionIds = data.region_ids.map((region: any) => region.id)

    setSaving(true)

    createFunderRegion(
      {
        resource: 'funder-regions',
        values: {
          funder_uuid: funderUuid,
          region_ids: {
            region_ids: regionIds,
            updated_by:
              user?.name ||
              `${user?.given_name || ''} ${user?.family_name || ''}`,
          },
        },
      },
      {
        onSuccess: () => {
          setSaving(false)
          invalidate({
            resource: 'funder-regions',
            invalidates: ['all'],
          })
        },
        onError: () => {
          setSaving(false)
          alert('Error creating location. Please try again.')
        },
      }
    )
  }

  function handleDeleteRegion(region_id: number) {
    if (window.confirm('Are you sure you want to remove this location?')) {
      deleteFunderRegion(
        {
          resource: 'funder-regions',
          id: funderUuid,
          meta: {
            region_id: region_id,
            updated_by:
              user?.name ||
              `${user?.given_name || ''} ${user?.family_name || ''}`,
          },
        },
        {
          onSuccess: () => {
            invalidate({
              resource: 'funder-regions',
              invalidates: ['all'],
            })
          },
          onError: () => {
            alert('Error removing location. Please try again.')
          },
        }
      )
    }
  }

  return (
    <Box>
      {/* Table for displaying locations */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Location Name</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {regionDataIsLoading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Loading locations...
                </TableCell>
              </TableRow>
            ) : regionData?.data && regionData?.data?.length > 0 ? (
              regionData?.data.map((region: any) => {
                const regionObj = regionNamesData?.data.find(
                  (r: any) => r.id === region.region_id
                )
                const hierarchy = [
                  regionObj?.country,
                  regionObj?.region,
                  regionObj?.council,
                  regionObj?.major,
                  regionObj?.suburb,
                ].filter(Boolean).join(' → ')
                return (
                <TableRow key={region.region_id}>
                  <TableCell>
                    <Tooltip title={hierarchy || ''} placement="top" slotProps={{ tooltip: { sx: { bgcolor: 'white', color: 'grey.700', boxShadow: 1, fontSize: '0.85rem', maxWidth: 400 } } }}>
                      <Link
                        component="button"
                        variant="body1"
                        onClick={(e) => {
                          e.preventDefault()
                          show('regions', region.region_id)
                        }}
                        sx={{
                          cursor: 'pointer',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        <strong>{regionObj?.name}</strong>
                      </Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDeleteRegion(region.region_id)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              )})
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No locations found for this funder
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Add Location
      </Typography>

      {/* Form for adding locations */}
      <Box
        component="form"
        onSubmit={handleSubmit(handleAddRegion)}
        sx={{ mb: 4 }}
      >
        <FormControl fullWidth>
          <Controller
            name="region_ids"
            control={control}
            defaultValue={[]}
            rules={{ required: 'At least one location is required' }}
            render={({ field, fieldState, formState }) => (
              <Autocomplete
                {...field}
                multiple
                value={field.value || []}
                onChange={(_event, newValues: RegionOption[]) => {
                  const regionSets = newValues.filter(isRegionSet)
                  const regions = newValues.filter(
                    (v): v is Region => !isRegionSet(v),
                  )

                  if (regionSets.length > 0) {
                    const allRegionIds = new Set(regions.map((r) => r.id))
                    regionSets.forEach((set) => {
                      set.region_ids.forEach((id) => allRegionIds.add(id))
                    })
                    const expandedRegions = (
                      regionOptions?.data as Region[] || []
                    ).filter((r) => allRegionIds.has(r.id))
                    field.onChange(expandedRegions)
                  } else {
                    field.onChange(regions)
                  }
                }}
                options={[
                  ...(regionSetOptions?.data || []).map(
                    (set): RegionSet => ({ ...(set as Omit<RegionSet, 'isLocationSet'>), isLocationSet: true }),
                  ),
                  ...(regionOptions?.data as Region[] || []),
                ] as RegionOption[]}
                getOptionLabel={(option) =>
                  'isLocationSet' in option
                    ? `${option.name} (Location Set)`
                    : option.name
                }
                isOptionEqualToValue={(option, value) => {
                  if ('isLocationSet' in option) return false
                  return option.id === value.id
                }}
                filterOptions={(options, params) => {
                  const inputValue = params.inputValue.toLowerCase().trim()
                  if (!inputValue) return []
                  const asciiInput = getAsciiName(inputValue)

                  const filtered = options.filter((option) => {
                    if (option.ascii_name) {
                      if (option.ascii_name.toLowerCase().includes(asciiInput)) return true
                    } else if (getAsciiName(option.name).includes(asciiInput)) {
                      return true
                    }
                    if (
                      'additional_names' in option &&
                      option.additional_names &&
                      option.additional_names.length > 0
                    ) {
                      return option.additional_names.some((n: string) =>
                        getAsciiName(n).toLowerCase().includes(asciiInput)
                      )
                    }
                    return false
                  })

                  return filtered.sort((a, b) => {
                    const aIsSet = 'isLocationSet' in a
                    const bIsSet = 'isLocationSet' in b
                    if (aIsSet && !bIsSet) return -1
                    if (bIsSet && !aIsSet) return 1
                    const aName = (a.ascii_name || a.name).toLowerCase()
                    const bName = (b.ascii_name || b.name).toLowerCase()
                    if (aName === asciiInput) return -1
                    if (bName === asciiInput) return 1
                    if (aName.startsWith(asciiInput) && !bName.startsWith(asciiInput)) return -1
                    if (bName.startsWith(asciiInput) && !aName.startsWith(asciiInput)) return 1
                    return aName.length - bName.length
                  })
                }}
                renderOption={(props, option) => {
                  if ('isLocationSet' in option) {
                    return (
                      <li {...props} key={`set-${option.id}`}>
                        <div>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              component="span"
                              sx={{
                                bgcolor: '#1C3045',
                                color: 'white',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                fontSize: '0.75em',
                                fontWeight: 'bold',
                              }}
                            >
                              GROUP
                            </Box>
                            <strong>{option.name}</strong>
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85em' }}>
                            {formatGroupLocationsPreview(option.region_ids, regionNameById)}
                          </Typography>
                          <AliasHint names={option.additional_names} />
                        </div>
                      </li>
                    )
                  }
                  const hierarchy = [
                    option.country,
                    option.region,
                    option.council,
                    option.major,
                    option.suburb,
                  ]
                    .filter(Boolean)
                    .join(' → ')

                  return (
                    <li {...props} key={option.id}>
                      <div>
                        <strong>{option.name}</strong>
                        {hierarchy && (
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9em' }}>
                            {hierarchy}
                          </Typography>
                        )}
                        <AliasHint names={option.additional_names} />
                      </div>
                    </li>
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search locations"
                    placeholder="Search locations..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index })
                    return <Chip key={key} label={option.name} {...tagProps} />
                  })
                }
              />
            )}
          />
          {errors.region_id && (
            <Typography color="error" variant="body2">
              {errors.region_id.message?.toString() || 'Error adding Locations'}
            </Typography>
          )}
        </FormControl>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
        >
          {saving ? 'Saving...' : 'Add Location'}
        </Button>
      </Box>
    </Box>
  )
}
